import type { QaSchedule, QaScheduleDraft } from '@/features/project-board/types';

const DATE = /\d{4}-\d{2}-\d{2}/g;

/** 색인된 QA 일정을 화면 한 줄로 쓰는 표기. */
export class QaScheduleLabel {
  /** `2026-10-12` → `10-12`. 같은 해 안의 일정을 좁은 카드에 싣기 위한 표기다. */
  static shortDate(date: string): string {
    return date.slice(5);
  }

  /** 검증 기간 한 줄. 하루짜리면 날짜 하나, 종료일이 없으면 `~ 미정`, 날짜가 없으면 `null`. */
  static period(schedule: QaSchedule | null): string | null {
    if (!schedule?.reviewStart) return null;
    if (!schedule.reviewEnd) return `${schedule.reviewStart} ~ 미정`;
    return schedule.reviewEnd === schedule.reviewStart ? schedule.reviewStart : `${schedule.reviewStart} ~ ${schedule.reviewEnd}`;
  }

  /** 검증 기간 끝 표기 — 하루짜리면 없음, 종료일 미정이면 `~미정`. */
  static endSuffix(schedule: QaSchedule): string {
    if (!schedule.reviewEnd) return '~미정';
    return schedule.reviewEnd === schedule.reviewStart ? '' : `~${QaScheduleLabel.shortDate(schedule.reviewEnd)}`;
  }

  /** 카드·표에 싣는 짧은 일정 줄. 일정이 없으면 `null` — "미정"을 일정처럼 보이지 않는다. */
  static brief(schedule: QaSchedule | null): string | null {
    if (!schedule) return null;
    const parts = [
      schedule.reviewStart ? `검증 ${QaScheduleLabel.shortDate(schedule.reviewStart)}${QaScheduleLabel.endSuffix(schedule)}` : null,
      schedule.releaseTarget ? `배포 ${QaScheduleLabel.shortDate(schedule.releaseTarget)}` : null,
    ].filter((part): part is string => part !== null);
    return parts.length > 0 ? parts.join(' · ') : null;
  }
}

/** 역기획서(`nd-qa-brief.md`)와 초안(`nd-qa-brief.draft.md`)의 `## QA 일정` 절을 입력 칸으로 다루는 규칙. */
export class QaBriefSchedule {
  /** QA 일정을 적을 수 있는 문서인가 — 역기획서와 그 초안(서버와 같은 기준). */
  static isBrief(path: string): boolean {
    return path.endsWith('/nd-qa-brief.md') || path.endsWith('/nd-qa-brief.draft.md');
  }

  /**
   * 역기획서 본문의 `## QA 일정` 절을 입력 칸의 초기값으로 읽는다. 절이 없으면 빈 칸이다.
   *
   * 보드 색인의 일정(`QaSchedule`)을 쓰지 않는 이유: 색인은 `미정 — <담당>`의 담당을 버린다. 화면이 보고 있는
   * 본문이 입력의 출발점이어야 한다.
   */
  static draftFrom(text: string): QaScheduleDraft {
    const section = text.match(/^##\s+QA 일정\s*$([\s\S]*?)(?=^##\s|(?![\s\S]))/m)?.[1] ?? '';
    const field = (label: string): string =>
      section.match(new RegExp(`^ {0,3}[-*]\\s+${label}\\s*:\\s*(.*)$`, 'm'))?.[1]?.trim() ?? '';
    const review = field('QA 검증 기간').match(DATE) ?? [];
    const release = field('배포 목표일');
    const releaseDate = release.match(DATE)?.[0] ?? '';
    const note = field('일정 제약·메모');
    return {
      reviewStart: review[0] ?? '',
      reviewEnd: review[1] ?? '',
      releaseTarget: releaseDate,
      releaseOwner: releaseDate ? '' : (release.match(/^미정\s*[—-]\s*(.+)$/)?.[1]?.trim() ?? ''),
      note: note === '없음' ? '' : note,
    };
  }

  /** 저장하기 전에 빠진 값. 없으면 `null`. 서버(`QaScheduleEntry`)가 같은 규칙으로 다시 거르고, 여기서는 단추만 막는다. */
  static problemOf(draft: QaScheduleDraft): string | null {
    if (draft.reviewStart === '') return '검증 시작일을 입력한다';
    return null;
  }
}
